import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { getWorkspaceARI } from '@confluence/theming-utils/entry-points/getWorkspaceARI.ts';

import type {
	WorkspaceThemePreferenceQuery as WorkspaceThemePreferenceQueryType,
	WorkspaceThemePreferenceQueryVariables,
} from './__types__/WorkspaceThemePreferenceQuery.ts';
import { WorkspaceThemePreferenceQuery } from './WorkspaceThemePreferenceQuery.graphql.ts';

/**
 * Fetches theme from shared user preferences API (AGG client).
 * This is used when the 'shared-user-preferences' feature flag is enabled.
 *
 * @returns Theme string or null if fetch fails or required params are missing
 */
export const preloadWorkspaceThemeForSSR = async (): Promise<string | null> => {
	const { cloudId, activationId, userId } = getPreloaderFnContext();

	// Can only fetch shared theme when we have all required params
	if (!userId || !cloudId || !activationId) {
		return null;
	}

	try {
		const workspaceAri = getWorkspaceARI({ cloudId, activationId });
		const response = await queryAgg<
			WorkspaceThemePreferenceQueryType,
			WorkspaceThemePreferenceQueryVariables
		>({
			/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
			query: WorkspaceThemePreferenceQuery,
			variables: {
				workspaceAri,
				accountId: userId,
			},
		});

		return response?.data?.settings_userPreferencesResolved?.theme || null;
	} catch {
		// Errors are handled by waitForAllReturnWhenError in the parent function
		return null;
	}
};
